import { Icon, Stack, TextField } from '@fluentui/react';
import { Button, buttonStylesPrimary, IconName, useTheme } from '@h2oai/ui-kit';

import { sanitizeS3ObjectKey, type ValidationResult } from '../../../../drive/s3Validation';
import { joinedButtonsStyles } from '../../../../utils/styles';

interface FileValidationItemProps {
  originalName: string;
  correctedName: string;
  validationResult: ValidationResult;
  overrideExisting: boolean;
  skip: boolean;
  onNameChange: (newName: string) => void;
  onOverrideClick: () => void;
  onSkipClick: () => void;
  index: number;
}

export const FileValidationItem = (props: FileValidationItemProps) => {
  const {
    originalName,
    correctedName,
    validationResult,
    overrideExisting,
    skip,
    onNameChange,
    onOverrideClick,
    onSkipClick,
  } = props;
  const { palette } = useTheme();
  const isDuplicate = validationResult.errors.some((error) => error.includes('already exists'));
  const isValid = validationResult.isValid;

  const handleFixClick = () => {
    const sanitizedName = sanitizeS3ObjectKey(originalName);
    onNameChange(sanitizedName);
  };

  const getErrorMessage = () => {
    if (isValid) return undefined;
    if (isDuplicate && (overrideExisting || skip)) return undefined;
    return validationResult.errors.join(', ');
  };

  const getDescription = () => {
    if (isDuplicate && overrideExisting) return '⚠️This file will override the existing one';
    if (isDuplicate && skip) return '⚠️This file will be skipped';

    return undefined;
  };

  const renderActionButtons = () => {
    return (
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        {isDuplicate && (
          <Stack horizontal styles={joinedButtonsStyles}>
            <Button
              text="Override"
              onClick={onOverrideClick}
              styles={overrideExisting ? buttonStylesPrimary : undefined}
            />
            <Button text="Skip" onClick={onSkipClick} styles={skip ? buttonStylesPrimary : undefined} />
          </Stack>
        )}

        {isValid && !isDuplicate ? (
          <Icon iconName={IconName.CheckMark} style={{ fontSize: 16, lineHeight: '28px', color: palette?.green500 }} />
        ) : (
          !isDuplicate && <Button text="Fix" onClick={handleFixClick} />
        )}
      </Stack>
    );
  };

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <div>
        <strong>Original:</strong> {originalName}
      </div>
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <TextField
          styles={{ root: { flexGrow: 1 } }}
          value={correctedName}
          onChange={(_, newValue) => onNameChange(newValue || '')}
          errorMessage={getErrorMessage()}
          description={getDescription()}
          placeholder="Enter a valid file name"
        />
        {renderActionButtons()}
      </Stack>
    </Stack>
  );
};
